import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { AdminApiService } from '../admin-api.service';
import { Permission, SystemApp } from '../models';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

interface ResourceGroup {
  system: string;
  resource: string;
  label: string;
  icon: string;
  permissions: Permission[];
  expanded: boolean;
}

interface SystemCard {
  system: SystemApp;
  total: number;
  active: number;
  inactive: number;
  resources: number;
  topResources: string[];
  icon: string;
  themeClass: string;
}

@Component({
  standalone: true,
  selector: 'app-permissions-page',
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    DialogModule,
    DropdownModule,
    InputTextModule,
    SelectButtonModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule,
  ],
  providers: [MessageService, ConfirmationService],
  styleUrls: ['./permissions.page.scss'],
  templateUrl: './permissions.page.html',
})
export class PermissionsPage implements OnInit {
  api = inject(AdminApiService);
  ms = inject(MessageService);
  confirmService = inject(ConfirmationService);

  systems: SystemApp[] = [];
  systemCards: SystemCard[] = [];
  allPermissions: Permission[] = [];

  // Detail view state
  selectedSystem: SystemApp | null = null;
  rows: Permission[] = [];
  groups: ResourceGroup[] = [];

  filter = { search: '' };

  loading = false;
  show = false;
  form: Permission = { systemKey: '', code: '', description: '' };

  // verb → semantic class
  private static readonly VERB_TONE: Record<string, string> = {
    read: 'tone-info',
    view: 'tone-info',
    list: 'tone-info',
    write: 'tone-success',
    create: 'tone-success',
    update: 'tone-warn',
    edit: 'tone-warn',
    delete: 'tone-danger',
    remove: 'tone-danger',
    approve: 'tone-success',
    reject: 'tone-danger',
    'pre-approve': 'tone-success',
    'pre-reject': 'tone-danger',
    accept: 'tone-success',
    invoice: 'tone-purple',
    transfer: 'tone-purple',
    deposit: 'tone-success',
    withdraw: 'tone-warn',
    collect: 'tone-success',
    send: 'tone-info',
    'send-bulk': 'tone-info',
    print: 'tone-neutral',
    export: 'tone-neutral',
  };

  // resource → icon (PrimeIcons)
  private static readonly RESOURCE_ICON: Record<string, string> = {
    '*': 'pi pi-star-fill',
    access: 'pi pi-key',
    users: 'pi pi-users',
    roles: 'pi pi-id-card',
    permissions: 'pi pi-shield',
    settings: 'pi pi-cog',
    reports: 'pi pi-chart-bar',
    patients: 'pi pi-user',
    samples: 'pi pi-flask',
    tests: 'pi pi-microchip',
    results: 'pi pi-check-square',
    orders: 'pi pi-shopping-cart',
    invoices: 'pi pi-file',
    payments: 'pi pi-credit-card',
    hr: 'pi pi-briefcase',
    employees: 'pi pi-user-edit',
    salaries: 'pi pi-dollar',
    inventory: 'pi pi-box',
    suppliers: 'pi pi-truck',
    customers: 'pi pi-users',
    audit: 'pi pi-history',
    logs: 'pi pi-list',
  };

  // system key → theme + icon
  private static readonly SYSTEM_THEME: Record<string, { icon: string; themeClass: string }> = {
    lab: { icon: 'pi pi-microchip', themeClass: 'theme-lab' },
    curalab: { icon: 'pi pi-microchip', themeClass: 'theme-lab' },
    hr: { icon: 'pi pi-briefcase', themeClass: 'theme-hr' },
    insurance: { icon: 'pi pi-shield', themeClass: 'theme-insurance' },
    auth: { icon: 'pi pi-key', themeClass: 'theme-auth' },
    system: { icon: 'pi pi-server', themeClass: 'theme-system' },
  };

  ngOnInit() {
    this.loadCatalog();
  }

  // ============== Initial catalog (systems + all permissions) ==============
  async loadCatalog() {
    this.loading = true;
    try {
      const [systems, allPerms] = await Promise.all([
        firstValueFrom(this.api.listSystems()),
        this.api.listAllPermissions(),
      ]);
      this.systems = (systems ?? []).filter((s) => s.isActive !== false);
      this.allPermissions = allPerms ?? [];
      this.systemCards = this.buildSystemCards();
    } catch (err) {
      console.error('Error loading catalog:', err);
      this.ms.add({ severity: 'error', summary: 'خطأ', detail: 'فشل تحميل الأنظمة' });
    } finally {
      this.loading = false;
    }
  }

  private buildSystemCards(): SystemCard[] {
    const cards: SystemCard[] = [];
    for (const sys of this.systems) {
      const perms = this.allPermissions.filter((p) => p.systemKey === sys.key);
      const resources = new Map<string, number>();
      let active = 0;
      let inactive = 0;
      for (const p of perms) {
        const dotIdx = p.code.indexOf('.');
        const resource = dotIdx === -1 ? p.code : p.code.substring(0, dotIdx);
        resources.set(resource, (resources.get(resource) ?? 0) + 1);
        if (p.isActive !== false) active++;
        else inactive++;
      }
      const theme = PermissionsPage.SYSTEM_THEME[sys.key.toLowerCase()] ?? {
        icon: 'pi pi-th-large',
        themeClass: 'theme-default',
      };
      cards.push({
        system: sys,
        total: perms.length,
        active,
        inactive,
        resources: resources.size,
        topResources: Array.from(resources.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 4)
          .map(([r]) => (r === '*' ? '*' : this.humanizeResource(r))),
        icon: theme.icon,
        themeClass: theme.themeClass,
      });
    }
    // hide systems with zero permissions to keep the picker focused
    return cards.sort((a, b) => b.total - a.total);
  }

  // ============== Detail view ==============
  selectSystem(card: SystemCard) {
    if (card.total === 0) {
      this.ms.add({ severity: 'info', summary: 'لا توجد صلاحيات', detail: `لم تُسجَّل صلاحيات لهذا النظام بعد.` });
      return;
    }
    this.selectedSystem = card.system;
    this.filter.search = '';
    this.applyFilters();
    // Expand the first 2 groups for an inviting first impression
    queueMicrotask(() => {
      for (let i = 0; i < Math.min(2, this.groups.length); i++) this.groups[i].expanded = true;
    });
  }

  backToPicker() {
    this.selectedSystem = null;
    this.rows = [];
    this.groups = [];
    this.filter.search = '';
  }

  applyFilters() {
    if (!this.selectedSystem) return;
    const systemKey = this.selectedSystem.key;
    const q = (this.filter.search ?? '').trim().toLowerCase();
    this.rows = this.allPermissions.filter((p) => {
      if (p.systemKey !== systemKey) return false;
      if (!q) return true;
      return (
        p.code?.toLowerCase().includes(q) ||
        (p.displayNameAr ?? '').toLowerCase().includes(q) ||
        (p.displayNameEn ?? '').toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q)
      );
    });
    this.buildGroups();
  }

  clearFilters() {
    this.filter.search = '';
    this.applyFilters();
  }

  // ============== Stats (current view) ==============
  get currentSystemIcon(): string {
    if (!this.selectedSystem) return 'pi pi-th-large';
    const theme = PermissionsPage.SYSTEM_THEME[this.selectedSystem.key.toLowerCase()];
    return theme?.icon ?? 'pi pi-th-large';
  }
  get currentTotal(): number {
    return this.rows.length;
  }
  get currentActive(): number {
    return this.rows.filter((p) => p.isActive !== false).length;
  }
  get currentInactive(): number {
    return this.rows.filter((p) => p.isActive === false).length;
  }
  get currentResources(): number {
    return this.groups.length;
  }

  // ============== Grouping ==============
  private buildGroups() {
    const map = new Map<string, ResourceGroup>();
    for (const p of this.rows) {
      if (!p.systemKey || !p.code) continue;
      const dotIdx = p.code.indexOf('.');
      const resource = dotIdx === -1 ? p.code : p.code.substring(0, dotIdx);
      const groupKey = `${p.systemKey}::${resource}`;
      let g = map.get(groupKey);
      if (!g) {
        g = {
          system: p.systemKey,
          resource,
          label: this.humanizeResource(resource),
          icon: this.iconForResource(resource),
          permissions: [],
          expanded: false,
        };
        map.set(groupKey, g);
      }
      g.permissions.push(p);
    }
    const actionOrder = ['read', 'write', 'delete', 'approve', 'reject', 'pre-approve', 'pre-reject', 'accept', 'invoice', 'transfer', 'deposit', 'withdraw', 'collect', 'send', 'send-bulk'];
    for (const g of map.values()) {
      g.permissions.sort((a, b) => {
        const aAction = this.actionOf(a);
        const bAction = this.actionOf(b);
        const ai = actionOrder.indexOf(aAction);
        const bi = actionOrder.indexOf(bAction);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return aAction.localeCompare(bAction);
      });
    }
    this.groups = Array.from(map.values()).sort((a, b) => a.resource.localeCompare(b.resource));
  }

  private humanizeResource(resource: string): string {
    if (resource === '*') return 'كل الصلاحيات (Wildcard)';
    if (resource === 'access') return 'الوصول العام';
    return resource.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private iconForResource(resource: string): string {
    return PermissionsPage.RESOURCE_ICON[resource] || 'pi pi-folder';
  }

  actionOf(p: Permission): string {
    if (!p.code) return '';
    const idx = p.code.indexOf('.');
    return idx === -1 ? p.code : p.code.substring(idx + 1);
  }

  verbTone(p: Permission): string {
    const verb = this.actionOf(p).toLowerCase();
    return PermissionsPage.VERB_TONE[verb] || 'tone-neutral';
  }

  expandAll() {
    for (const g of this.groups) g.expanded = true;
  }
  collapseAll() {
    for (const g of this.groups) g.expanded = false;
  }

  // ============== Mutations ==============
  openNew() {
    this.form = {
      systemKey: this.selectedSystem?.key ?? '',
      code: '',
      description: '',
    };
    this.show = true;
  }

  save() {
    if (!this.form.systemKey?.trim() || !this.form.code?.trim()) {
      this.ms.add({ severity: 'warn', summary: 'تحذير', detail: 'النظام والـ Code مطلوبان' });
      return;
    }

    this.api.createPermission(this.form).subscribe({
      next: () => {
        this.ms.add({ severity: 'success', summary: 'تم', detail: 'تم إضافة الصلاحية بنجاح' });
        this.show = false;
        this.reloadAfterMutation();
      },
      error: (err) => {
        const errorMsg = err?.error?.message || 'تعذر إضافة الصلاحية';
        this.ms.add({ severity: 'error', summary: 'خطأ', detail: errorMsg });
      },
    });
  }

  remove(r: Permission) {
    if (!r.id) return;
    this.confirmService.confirm({
      message: `هل أنت متأكد من حذف الصلاحية "${r.systemKey}:${r.code}"؟`,
      header: 'تأكيد الحذف',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'نعم',
      rejectLabel: 'لا',
      accept: () => {
        this.api.deletePermission(r.id!.toString()).subscribe({
          next: () => {
            this.ms.add({ severity: 'success', summary: 'تم', detail: 'تم حذف الصلاحية بنجاح' });
            this.reloadAfterMutation();
          },
          error: (err) => {
            const errorMsg = err?.error?.message || 'تعذر حذف الصلاحية';
            this.ms.add({ severity: 'error', summary: 'خطأ', detail: errorMsg });
          },
        });
      },
    });
  }

  private async reloadAfterMutation() {
    const keepSystemKey = this.selectedSystem?.key ?? null;
    await this.loadCatalog();
    if (keepSystemKey) {
      const stayed = this.systemCards.find((c) => c.system.key === keepSystemKey);
      if (stayed) {
        this.selectedSystem = stayed.system;
        this.applyFilters();
      } else {
        this.selectedSystem = null;
      }
    }
  }

  trackGroup = (_: number, g: ResourceGroup) => `${g.system}::${g.resource}`;
  trackPerm = (_: number, p: Permission) => p.id ?? `${p.systemKey}:${p.code}`;
  trackCard = (_: number, c: SystemCard) => c.system.key;
}
